import { View, Text, Pressable, Platform, ScrollView } from "react-native";
import { Link, router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";

/**
 * Navigation link card component for the landing page
 */
interface NavCardProps {
  href: string;
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  color: string;
}

const NavCard = ({ href, icon, title, description, color }: NavCardProps) => (
  <Link href={href as any} asChild>
    <Pressable
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
      style={{ cursor: "pointer" } as any}
    >
      <View className="flex-row items-center gap-4">
        <View
          className="w-14 h-14 rounded-xl items-center justify-center"
          style={{ backgroundColor: color + "15" }}
        >
          <Ionicons name={icon} size={28} color={color} />
        </View>
        <View className="flex-1">
          <Text className="text-lg font-semibold text-gray-900">{title}</Text>
          <Text className="text-sm text-gray-500 mt-1">{description}</Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color="#9CA3AF" />
      </View>
    </Pressable>
  </Link>
);

/**
 * Live Quiz Display Card for admins
 */
const LiveQuizCard = ({ sessionId, quizName }: { sessionId: string; quizName: string }) => (
  <Pressable
    onPress={() => router.push(`/(web)/quiz-display?sessionId=${sessionId}` as any)}
    className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
    style={{ cursor: "pointer" } as any}
  >
    <View className="flex-row items-center gap-4">
      <View className="w-14 h-14 rounded-xl items-center justify-center bg-white/20">
        <Ionicons name="tv" size={28} color="#FFFFFF" />
      </View>
      <View className="flex-1">
        <View className="flex-row items-center gap-2 mb-1">
          <View className="w-2 h-2 rounded-full bg-green-400" />
          <Text className="text-xs font-bold text-green-300 uppercase">Live Quiz</Text>
        </View>
        <Text className="text-lg font-semibold text-white">{quizName}</Text>
        <Text className="text-sm text-violet-200 mt-1">Klik om quiz display te openen</Text>
      </View>
      <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
    </View>
  </Pressable>
);

/**
 * Feature card for the explainer section
 */
interface FeatureCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  color: string;
}

const FeatureCard = ({ icon, title, description, color }: FeatureCardProps) => (
  <View className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex-1 min-w-[280px]">
    <View
      className="w-12 h-12 rounded-lg items-center justify-center mb-3"
      style={{ backgroundColor: color + "15" }}
    >
      <Ionicons name={icon} size={24} color={color} />
    </View>
    <Text className="text-base font-semibold text-gray-900 mb-1">{title}</Text>
    <Text className="text-sm text-gray-500 leading-5">{description}</Text>
  </View>
);

/**
 * Web landing page with navigation to legal and support pages
 */
export default function WebLandingPage() {
  const [activeSession, setActiveSession] = useState<{ id: string; quizName: string } | null>(null);

  // Check for active quiz sessions
  const checkForActiveQuiz = useCallback(async () => {
    try {
      const { data: sessions } = await supabase
        .from("quiz_live_sessions")
        .select(`
          id,
          quiz:quizzes (name)
        `)
        .in("status", ["lobby", "playing", "section_break", "showing_ranking"])
        .order("created_at", { ascending: false })
        .limit(1);
      
      if (sessions && sessions.length > 0) {
        const session = sessions[0] as any;
        setActiveSession({
          id: session.id,
          quizName: session.quiz?.name || "Live Quiz",
        });
      } else {
        setActiveSession(null);
      }
    } catch (err) {
      console.error("Error checking for active quiz:", err);
    }
  }, []);
  
  useEffect(() => {
    if (Platform.OS !== "web") return;
    
    checkForActiveQuiz();
    
    // Subscribe to changes
    const channel = supabase
      .channel("web_active_quiz")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quiz_live_sessions",
        },
        () => checkForActiveQuiz()
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [checkForActiveQuiz]);
  
  // Only render on web platform
  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <ScrollView className="flex-1 min-h-screen">
      {/* Header */}
      <View className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-16 md:py-24">
        <View className="max-w-2xl mx-auto items-center">
          {/* Logo/Icon */}
          <View className="w-20 h-20 bg-white/20 rounded-3xl items-center justify-center mb-6 backdrop-blur-sm">
            <Text className="text-4xl">👨‍👩‍👧‍👦</Text>
          </View>

          <Text className="text-3xl md:text-4xl font-bold text-white text-center">
            Staatsen Familie App
          </Text>

          <Text className="text-lg text-violet-100 text-center mt-4 max-w-md">
            De app voor de Staatsen familie. Samen spelen, samen verbonden.
          </Text>

          {/* Guide CTA */}
          <Link href="/(web)/guide" asChild>
            <Pressable
              className="mt-8 flex-row items-center gap-2 px-6 py-3 rounded-full bg-white/20 hover:bg-white/30 active:bg-white/10 backdrop-blur-sm transition-colors"
              style={{ cursor: "pointer" } as any}
            >
              <Ionicons name="book" size={20} color="#FFFFFF" />
              <Text className="text-base font-semibold text-white">
                Bekijk de handleiding
              </Text>
              <Ionicons name="arrow-forward" size={16} color="#FFFFFF" />
            </Pressable>
          </Link>
        </View>
      </View>
        {/* Active Quiz Card */}
        {activeSession && (
          <View className="px-6 py-8 bg-violet-50">
            <View className="max-w-4xl mx-auto w-full">
              <Text className="text-2xl font-bold text-gray-900 mb-2">
                🎯 Live Quiz Actief
              </Text>
              <Text className="text-gray-500 mb-4">
                Er is een live quiz gaande. Open de display voor presentatie.
              </Text>
              <LiveQuizCard 
                sessionId={activeSession.id} 
                quizName={activeSession.quizName} 
              />
            </View>
          </View>
        )}

        {/* App Features Explainer */}
        <View className="px-6 py-12 bg-gray-50">
          <View className="max-w-4xl mx-auto w-full">
            <Text className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
              Wat kun je doen met de app?
            </Text>
            <Text className="text-gray-500 text-center mb-8 max-w-2xl mx-auto">
              De Staatsen Familie App brengt de familie samen tijdens het familie weekend en daarbuiten. 
              Hier is een overzicht van alle mogelijkheden.
            </Text>

            {/* Feature Grid */}
            <View className="flex-row flex-wrap gap-4 justify-center mb-12">
              <FeatureCard
                icon="chatbubbles"
                title="Familie Chat"
                description="Blijf in contact met de hele familie. Stuur berichten, foto's en maak polls in groepschats of privéberichten."
                color="#7C3AED"
              />
              <FeatureCard
                icon="game-controller"
                title="Spellen Spelen"
                description="Speel Memory, Dammen, Sudoku, Match 3 en meer! Daag familieleden uit en verdien punten voor je team."
                color="#3B82F6"
              />
              <FeatureCard
                icon="trophy"
                title="Punten & Leaderboard"
                description="Verdien punten door spellen te winnen en uitdagingen te voltooien. Bekijk de teamstand en individuele scores."
                color="#F59E0B"
              />
              <FeatureCard
                icon="camera"
                title="Foto Opdrachten"
                description="Maak foto's voor leuke opdrachten en verdien punten. Upload je teamfoto en deel herinneringen."
                color="#8B5CF6"
              />
              <FeatureCard
                icon="calendar"
                title="Agenda"
                description="Bekijk het programma van het weekend. Voeg events toe aan je eigen agenda en krijg herinneringen."
                color="#10B981"
              />
              <FeatureCard
                icon="people"
                title="Teams"
                description="Bekijk je teamgenoten, pas je teamavatar aan en werk samen om de meeste punten te verdienen."
                color="#EC4899"
              />
            </View>

            {/* Role Explainers */}
            <Text className="text-xl font-bold text-gray-900 text-center mb-6">
              Rollen in de app
            </Text>

            <View className="gap-4 mb-8">
              {/* Regular User */}
              <View className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <View className="flex-row items-start gap-4">
                  <View className="w-10 h-10 rounded-lg items-center justify-center bg-blue-100">
                    <Ionicons name="person" size={20} color="#3B82F6" />
                  </View>
                  <View className="flex-1">
                    <Text className="text-base font-semibold text-gray-900">Familielid</Text>
                    <Text className="text-sm text-gray-500 mt-1 leading-5">
                      Als familielid kun je chatten, spellen spelen, foto's uploaden, opdrachten voltooien 
                      en punten verdienen voor je team. Je kunt ook de agenda bekijken en herinneringen instellen.
                    </Text>
                  </View>
                </View>
              </View>

              {/* Familie Admin */}
              <View className="bg-white rounded-xl p-5 shadow-sm border border-amber-200">
                <View className="flex-row items-start gap-4">
                  <View className="w-10 h-10 rounded-lg items-center justify-center bg-amber-100">
                    <Ionicons name="shield" size={20} color="#D97706" />
                  </View>
                  <View className="flex-1">
                    <Text className="text-base font-semibold text-gray-900">Familie Admin</Text>
                    <Text className="text-sm text-gray-500 mt-1 leading-5">
                      Familie admins (commissieleden) beheren het weekend. Ze kunnen teams samenstellen, 
                      uitdagingen aanmaken, punten toekennen, de agenda beheren, live quizzes hosten 
                      en speurtochten opzetten. Ze kunnen ook opdrachten aanmaken met meerdere secties.
                    </Text>
                  </View>
                </View>
              </View>

              {/* Super Admin */}
              <View className="bg-white rounded-xl p-5 shadow-sm border border-violet-200">
                <View className="flex-row items-start gap-4">
                  <View className="w-10 h-10 rounded-lg items-center justify-center bg-violet-100">
                    <Ionicons name="star" size={20} color="#7C3AED" />
                  </View>
                  <View className="flex-1">
                    <Text className="text-base font-semibold text-gray-900">Super Admin</Text>
                    <Text className="text-sm text-gray-500 mt-1 leading-5">
                      Super admins hebben volledige controle over de app. Ze kunnen weekenden aanmaken, 
                      gebruikers beheren, rollen toewijzen en systeem-brede instellingen aanpassen. 
                      Ze configureren ook de spelstandaardinstellingen.
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Games Section */}
        <View className="px-6 py-12">
          <View className="max-w-4xl mx-auto w-full">
            <Text className="text-xl font-bold text-gray-900 text-center mb-6">
              🎮 Beschikbare Spellen
            </Text>

            <View className="flex-row flex-wrap gap-3 justify-center mb-8">
              {[
                { name: "Memory", icon: "grid", color: "#8B5CF6" },
                { name: "Dammen", icon: "apps", color: "#10B981" },
                { name: "Sudoku", icon: "keypad", color: "#3B82F6" },
                { name: "Match 3", icon: "diamond", color: "#EC4899" },
                { name: "Endless Runner", icon: "walk", color: "#F59E0B" },
                { name: "Live Quiz", icon: "help-circle", color: "#EF4444" },
                { name: "Speurtocht", icon: "map", color: "#06B6D4" },
                { name: "Boter Kaas Eieren", icon: "close", color: "#6366F1" },
              ].map((game) => (
                <View
                  key={game.name}
                  className="flex-row items-center gap-2 px-4 py-2 rounded-full"
                  style={{ backgroundColor: game.color + "15" }}
                >
                  <Ionicons name={game.icon as any} size={16} color={game.color} />
                  <Text className="text-sm font-medium" style={{ color: game.color }}>
                    {game.name}
                  </Text>
                </View>
              ))}
            </View>

            <View className="bg-violet-50 rounded-2xl p-6 border border-violet-100">
              <View className="flex-row items-start gap-3">
                <Ionicons name="bulb" size={24} color="#7C3AED" />
                <View className="flex-1">
                  <Text className="text-sm text-violet-900 font-medium">
                    Weekend vs Vrij Spelen
                  </Text>
                  <Text className="text-sm text-violet-700 mt-1">
                    Tijdens een actief weekend kun je punten verdienen voor je team. 
                    Buiten het weekend kun je "vrij spelen" - oefenen zonder puntenstress!
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Info & Support Section */}
        <View className="px-6 py-12 bg-white">
          <View className="max-w-xl mx-auto w-full">
            <Text className="text-2xl font-bold text-gray-900 mb-2">
              Informatie & Support
            </Text>
            <Text className="text-gray-500 mb-8">
              Alles wat je moet weten over de Staatsen App
            </Text>

            {/* Navigation Cards */}
            <View className="gap-4">
              <NavCard
                href="/(web)/guide"
                icon="book"
                title="Handleiding"
                description="Uitgebreide uitleg over alle functies en spellen"
                color="#7C3AED"
              />

              <NavCard
                href="/(web)/privacy-policy"
                icon="shield-checkmark"
                title="Privacybeleid"
                description="Hoe we omgaan met jouw gegevens"
                color="#7C3AED"
              />

              <NavCard
                href="/(web)/terms"
                icon="document-text"
                title="Gebruiksvoorwaarden"
                description="Regels en voorwaarden voor gebruik"
                color="#3B82F6"
              />

              <NavCard
                href="/(web)/support"
                icon="help-circle"
                title="Support & Contact"
                description="Hulp nodig? Neem contact op"
                color="#10B981"
              />

              <NavCard
                href="/(web)/delete-account"
                icon="trash"
                title="Account Verwijderen"
                description="Instructies voor account verwijdering"
                color="#EF4444"
              />
            </View>

            {/* Footer note */}
            <View className="mt-12 p-6 bg-violet-50 rounded-2xl border border-violet-100">
              <View className="flex-row items-start gap-3">
                <Ionicons name="information-circle" size={24} color="#7C3AED" />
                <View className="flex-1">
                  <Text className="text-sm text-violet-900 font-medium">
                    Alleen voor familie
                  </Text>
                  <Text className="text-sm text-violet-700 mt-1">
                    Deze app is exclusief beschikbaar voor leden van de Staatsen
                    familie. Een uitnodiging is vereist om een account aan te
                    maken.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View className="bg-gray-100 border-t border-gray-200 px-6 py-8">
          <View className="max-w-xl mx-auto items-center">
            <Text className="text-sm text-gray-500 text-center">
              © {new Date().getFullYear()} Staatsen Familie App
            </Text>
            <Text className="text-xs text-gray-400 mt-2 text-center">
              Gemaakt met ❤️ voor de familie
            </Text>
          </View>
        </View>
    </ScrollView>
  );
}

